/**
 * Agenda fuer heute auf der Uebersicht (F-50): Termine, Schule als ein
 * Block und die unverplanten Fenster mit ihrer Laenge - eine kompakte Liste
 * statt eines eigenen Stundenrasters mit zweiter Scrollleiste. Das Raster
 * bleibt auf der Kalenderseite.
 *
 * Nur anschauen und hinklicken (Leitlinie 4): ein Termin oeffnet sich im
 * Kalender, ein freies Fenster fuehrt in die Tagesansicht zum Planen.
 */
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';

import { AreaDot } from '@/modules/areas/AreaDot';
import { useAreaColor, useAreaMap } from '@/modules/areas/useAreas';
import { useJetzt } from '@/modules/benachrichtigungen/useMeldungen';
import { useTagesrahmen } from '@/modules/zeit/useZeitverteilung';
import { useCalendarStore } from '@/store/calendar';
import { useUiStore } from '@/store/ui';
import { agenda, dauerText, uhrzeit, type AgendaZeile } from '@/domain/zeit/fenster';
import { datePart, minutesOfDay } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';
import type { CalendarOccurrence, LocalDate } from '@/domain/types';

const zeile = 'flex w-full items-center gap-2 rounded px-1 py-1 text-left text-sm';
const zeitSpalte = 'w-[6.5rem] shrink-0 tabular-nums';

export function AgendaHeute({ heute, occurrences }: { heute: LocalDate; occurrences: CalendarOccurrence[] }) {
  const navigate = useNavigate();
  const areaColor = useAreaColor();
  const areaMap = useAreaMap();
  const rahmen = useTagesrahmen();
  const jetztZeit = useJetzt();
  const setView = useUiStore((s) => s.setCalendarView);
  const setAnchor = useCalendarStore((s) => s.setAnchor);
  const select = useCalendarStore((s) => s.select);

  // Nach Mitternacht ist "heute" ein neuer Tag - bis dahin gilt die Minute von jetzt.
  const jetzt = datePart(jetztZeit) === heute ? minutesOfDay(jetztZeit) : undefined;
  const zeilen = agenda(occurrences, heute, rahmen, jetzt);

  function zumKalender(o?: CalendarOccurrence) {
    setView('tag');
    setAnchor(heute);
    if (o) select(o);
    navigate('/kalender');
  }

  if (zeilen.length === 0) return <p className="text-sm text-ink-faint">Heute keine Termine.</p>;

  // Ein ausgefallener Termin "laeuft" nicht - seine Zeit ist frei.
  const laeuft = (z: AgendaZeile) => jetzt !== undefined && z.art !== 'ganztags' && !(z.art === 'termin' && z.ausgefallen)
    && z.von <= jetzt && jetzt < z.bis;

  return (
    <ul aria-label="Agenda heute" className="flex flex-col">
      {zeilen.map((z) => {
        if (z.art === 'frei') {
          const ab = laeuft(z);
          return (
            <li key={`frei-${z.von}`}>
              <button type="button" onClick={() => zumKalender()} title="In der Tagesansicht planen"
                className={cn(zeile, 'text-ink-muted hover:bg-surface-raised hover:text-ink')}>
                <span className={cn(zeitSpalte, 'text-ink-faint')}>{uhrzeit(z.von)}–{uhrzeit(z.bis)}</span>
                {/* Leerer, gestrichelter Punkt: hier ist (noch) nichts - kein Bereich. */}
                <span aria-hidden="true" className="size-2 shrink-0 rounded-full border border-dashed border-ink-faint" />
                <span className="min-w-0 flex-1 truncate">{ab ? 'Jetzt unverplant' : 'Unverplant'}</span>
                {/* Die Laenge ist die eigentliche Information - sie wird nie abgeschnitten. */}
                <span className="shrink-0 tabular-nums">{ab ? 'noch ' : ''}{dauerText(z.bis - z.von)}</span>
              </button>
            </li>
          );
        }
        if (z.art === 'schule') {
          return (
            <li key="schule">
              <Link to="/schule" className={cn(zeile, laeuft(z) && 'bg-accent/10', z.vorbei ? 'text-ink-faint' : 'text-ink-muted', 'hover:bg-surface-raised hover:text-ink')}>
                <span className={zeitSpalte}>{uhrzeit(z.von)}–{uhrzeit(z.bis)}</span>
                <GraduationCap className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">Schule · {z.anzahl} {z.anzahl === 1 ? 'Stunde' : 'Stunden'}</span>
              </Link>
            </li>
          );
        }
        const o = z.occ;
        const bereich = areaMap.get(o.areaId ?? '')?.name;
        const vorbei = z.art === 'termin' && z.vorbei;
        const ausgefallen = z.art === 'termin' && z.ausgefallen;
        return (
          <li key={o.occurrenceId}>
            <button type="button" onClick={() => zumKalender(o)}
              className={cn(zeile, laeuft(z) && 'bg-accent/10', vorbei && 'text-ink-faint', 'hover:bg-surface-raised')}>
              <span className={cn(zeitSpalte, !vorbei && 'text-ink-faint')}>
                {z.art === 'ganztags' ? 'ganztags' : `${uhrzeit(z.von)}–${uhrzeit(z.bis)}`}
              </span>
              <AreaDot color={areaColor(o.areaId)} size="sm" name={bereich} />
              <span className={cn('min-w-0 flex-1 truncate', ausgefallen && 'line-through')}>{o.title || 'Ohne Titel'}</span>
              {ausgefallen && <span className="shrink-0 text-[11px] text-ink-faint">ausgefallen</span>}
              {laeuft(z) && <span className="shrink-0 text-[11px] font-medium text-accent-text">läuft</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
